import React, {
  createContext,
  useContext,
  ChangeEvent,
  useState,
  KeyboardEvent
} from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import RevealDropdown from 'components/base/RevealDropdown';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  createBXPGreeting,
  updateBXPGreeting,
  deleteBXPGreeting,
  BXPGreeting
} from 'services/hvaservicecampaign.service';

interface BXPGreetingsTableProps {
  greetings: BXPGreeting[];
  onGreetingsChange: () => Promise<void>;
}

const GreetingsChangeContext = createContext<(() => Promise<void>) | undefined>(
  undefined
);

const useGreetingsChange = () => {
  const context = useContext(GreetingsChangeContext);
  if (context === undefined) {
    throw new Error(
      'useGreetingsChange must be used within a GreetingsChangeProvider'
    );
  }
  return context;
};

type GreetingActionCellProps = {
  greeting: BXPGreeting;
};

const GreetingActionCell = ({ greeting }: GreetingActionCellProps) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // eslint-disable-next-line
  const [selectedGreeting, setSelectedGreeting] = useState<BXPGreeting | null>(null);
  const onGreetingsChange = useGreetingsChange();

  const handleCloseDeleteModal = () => setShowDeleteModal(false);
  const handleShowDeleteModal = () => setShowDeleteModal(true);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedGreeting(null);
  };

  const handleShowEditModal = () => {
    setSelectedGreeting(greeting);
    setShowEditModal(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await deleteBXPGreeting(greeting.id);
      console.log(`Greeting with ID: ${greeting.id} deleted successfully`);
      await onGreetingsChange();
    } catch (error) {
      console.error(`Error deleting Greeting with ID: ${greeting.id}`, error);
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedGreeting) {
      try {
        await updateBXPGreeting(selectedGreeting.id, selectedGreeting);
        await onGreetingsChange();
        handleCloseEditModal();
      } catch (error) {
        alert('Error updating greeting: ' + error);
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }

    setSelectedGreeting(prev => (prev ? { ...prev, [name]: newValue } : null));
  };

  return (
    <>
      <RevealDropdown btnClassName="p-2 pt-1 pb-1 fs-11">
        <Dropdown.Item onClick={handleShowEditModal}>
          <FontAwesomeIcon icon={faEdit} className="fs-8 p-1 pt-0 pb-0" />
          Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={handleShowDeleteModal} className="text-danger">
          <FontAwesomeIcon icon={faTrash} className="fs-8 p-1 pt-0 pb-0" />
          Remove
        </Dropdown.Item>
      </RevealDropdown>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this Greeting?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRemove}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Update Greeting</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={selectedGreeting?.shortname || ''}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="greetingdesc"
                value={selectedGreeting?.greetingdesc || ''}
                onChange={handleInputChange}
                required
                maxLength={64}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Play Type</Form.Label>
              <Form.Control
                type="text"
                name="playtype"
                value={selectedGreeting?.playtype || ''}
                onChange={handleInputChange}
                maxLength={16}
              />
            </Form.Group>
            <Form.Group className="mb-3 mt-4">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="silencedetection"
                    value="silencedetection"
                    checked={selectedGreeting?.silencedetection || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Silence Detection
                </label>
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>MRCP Reco Opts</Form.Label>
              <Form.Control
                type="text"
                name="mrcp_recogopts"
                value={selectedGreeting?.mrcp_recogopts || ''}
                onChange={handleInputChange}
                maxLength={128}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Detection Engine API</Form.Label>
              <Form.Control
                type="text"
                name="detection_engine_api"
                value={selectedGreeting?.detection_engine_api || ''}
                onChange={handleInputChange}
                maxLength={6}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    value="active"
                    checked={selectedGreeting?.active || false}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update Greeting
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export const bxpgreetingsTablecolumns: ColumnDef<BXPGreeting>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'shortname',
    header: 'Short Name',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'action',
    enableSorting: false,
    header: '',
    cell: ({ row: { original } }) => {
      return <GreetingActionCell greeting={original} />;
    },
    meta: {
      cellProps: { className: 'text-body-tertiary' }
    }
  },
  {
    accessorKey: 'greetingdesc',
    header: 'Description',
    meta: {
      headerProps: { style: { width: '25%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1' }
    }
  },
  {
    accessorKey: 'playtype',
    header: 'Play Type',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'silencedetection',
    header: 'Silence Detection',
    cell: ({ row }) => (row.original.silencedetection ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'mrcp_recogopts',
    header: 'MRCP Reco Opts',
    meta: {
      headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'detection_engine_api',
    header: 'Detection Engine API',
    meta: {
      headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'active',
    header: 'Active',
    cell: ({ row }) => (row.original.active ? 'Yes' : 'No'),
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
      cellProps: { className: 'px-1 white-space-nowrap' }
    }
  }
];

export const BXPGreetingsTable: React.FC<BXPGreetingsTableProps> = ({
  greetings,
  onGreetingsChange
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newGreeting, setNewGreeting] = useState<BXPGreeting>({
    id: 0,
    shortname: '',
    greetingdesc: '',
    playtype: '',
    silencedetection: false,
    mrcp_recogopts: '',
    detection_engine_api: '',
    active: false
  });
  const table = useAdvanceTable({
    data: greetings,
    columns: bxpgreetingsTablecolumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowAddModal = () => setShowAddModal(true);
  const handleCloseAddModal = () => setShowAddModal(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }

    setNewGreeting(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await createBXPGreeting(newGreeting);
      handleCloseAddModal();
      onGreetingsChange();
    } catch (error) {
      console.error('Error creating greeting:', error);
    }
  };

  return (
    <GreetingsChangeContext.Provider value={onGreetingsChange}>
      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3">
            <SearchBox
              placeholder="Search Greetings"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
            />
            <div className="ms-auto">
              <Button
                variant="primary"
                className="p-2"
                onClick={handleShowAddModal}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                Add Greeting
              </Button>
            </div>
          </div>
        </div>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
          />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>

      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Greeting</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Short Name</Form.Label>
              <Form.Control
                type="text"
                name="shortname"
                value={newGreeting.shortname}
                onChange={handleInputChange}
                required
                maxLength={32}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                name="greetingdesc"
                value={newGreeting.greetingdesc}
                onChange={handleInputChange}
                required
                maxLength={64}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Play Type</Form.Label>
              <Form.Control
                type="text"
                name="playtype"
                value={newGreeting.playtype}
                onChange={handleInputChange}
                maxLength={16}
              />
            </Form.Group>
            <Form.Group className="mb-3 mt-4">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="silencedetection"
                    checked={newGreeting.silencedetection}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Silence Detection
                </label>
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>MRCP Reco Opts</Form.Label>
              <Form.Control
                type="text"
                name="mrcp_recogopts"
                value={newGreeting.mrcp_recogopts}
                onChange={handleInputChange}
                maxLength={128}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Detection Engine API</Form.Label>
              <Form.Control
                type="text"
                name="detection_engine_api"
                value={newGreeting.detection_engine_api}
                onChange={handleInputChange}
                maxLength={6}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="form-check form-switch pb-1 pt-1">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={newGreeting.active}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  Active
                </label>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Add Greeting
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </GreetingsChangeContext.Provider>
  );
};
